import styled, { css } from 'vue-styled-components';
import COLORS from '@design/colors.js';
import {
    ON_MOBILE,
    FLEX_CENTER,
    applyHoverAndPress,
    ABSOLUTE_FULL_AREA,
    GRAY,
    RespondLandscape,
    RespondMobile,
    RespondPC,
} from '@styles/common.styled';

import {
    DIALOG_TEXT,
    FLEX_COLUMN_DIV,
    FLEX_VERTICAL_CENTER_DIV,
    FLEX_CENTER_DIV,
} from '@styles/common.styled.component';

import { tagNameEq } from '@util';
import { KEY_CODE } from '@consts';
import { fromEvent } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import CloseIcon from '@svgIcons/close.svg';

import RingLoader from '@components/Common/RingLoader';

const Progress = () => import('vue-multiple-progress');

var defaultCb = R.always(undefined);

export default {
    name: 'Dialog',
    props: {
        className: String,
        active: {
            type: Boolean,
            default: false,
        },
        cancelText: String,
        cancelType: String,
        confirmText: String,
        title: String,
        text: String,
        confirmType: {
            type: String,
            default: 'primary', // primary | danger
        },
        hideButtons: {
            type: Boolean,
            default: false,
        },
        confirmCb: {
            type: Function,
            default: defaultCb,
        },
        cancelCb: {
            type: Function,
            default: defaultCb,
        },
        child: Object,
        backdrop: {
            type: Boolean,
            default: false,
        },
        hasConfirmButton: {
            type: Boolean,
            default: true,
        },
        hasCancelButton: {
            type: Boolean,
            default: true,
        },
        buttonLoading: Boolean,
        buttonDisabled: Boolean,
        paddingLess: Boolean,
        loadingProgress: Boolean,
        emitConfirmOnEnter: {
            type: Boolean,
            default: false,
        },
        hasContentsMaxWidth: {
            type: Boolean,
            default: true,
        },
        hasCloseButton: {
            type: Boolean,
            default: false,
        },
        keyboard: {
            type: Boolean,
            default: false,
        },
        isFullOnMobile: {
            type: Boolean,
            default: false,
        },
        scrollOnBody: {
            type: Boolean,
            default: false,
        },
        autoSize: {
            type: Boolean,
            default: false,
        },
        emitCancelOnClickOutside: {
            type: Boolean,
            default: true,
        },
        newDesign: {
            type: Boolean,
            default: false,
        },
        customCss: {
            type: String,
            default: '',
        },
    },
    watch: {
        active: {
            handler(value) {
                this.clipHtml(value);
            },
            immediate: true,
        },
    },
    beforeDestroy() {
        this.clipHtml(false);
    },
    destroyed() {
        this.$emit('destroyed');
    },
    mounted() {
        this.$emit('mounted');
        if (this.emitConfirmOnEnter) {
            this.$nextTick(() => this.watchEnter$());
        }

        this.observeEsc$();
    },
    render() {
        if (!this._active) {
            return null;
        }

        const {
            newDesign,
            scrollOnBody,
            renderFull,
            renderNormal,
            className,
            customCss,
        } = this;

        return (
            <Wrapper
                newDesign={newDesign}
                active={this._active}
                isFullOnMobile={this.isFullOnMobile}
                class={[
                    'adriel-dialog',
                    { 'scroll-on-body': scrollOnBody },
                    className,
                ]}
                customCss={customCss}
            >
                {scrollOnBody ? renderFull() : renderNormal()}
            </Wrapper>
        );
    },
    computed: {
        _active: {
            get: R.prop('active'),
            set(value) {
                this.$emit('update:active', value);
            },
        },
        _cancelText() {
            return this.cancelText || this.$t('COMMON.cancel');
        },
        _confirmText() {
            return this.confirmText || this.$t('COMMON.ok');
        },
    },
    methods: {
        watchEnter$() {
            const enter$ = fromEvent(document, 'keyup').pipe(
                filter(
                    R.both(
                        R.propEq('keyCode', KEY_CODE.enter),
                        tagNameEq('input')
                    )
                )
            );

            this.$subscribeTo(enter$, () => this.$emit('confirm'));
        },
        observeEsc$() {
            const { generateWatchNew$: gen$ } = this;

            const esc$ = gen$('keyboard').pipe(
                filter(R.identity),
                switchMap(() => {
                    return fromEvent(document, 'keyup').pipe(
                        filter(R.propEq('keyCode', KEY_CODE.esc))
                    );
                })
            );

            this.$subscribeTo(esc$, () => {
                this._active = false;
                this.$emit('cancel');
            });
        },
        handleCancel() {
            this.cancelCb();
            this.$emit('cancel');
        },
        onClickOutside() {
            if (this.backdrop !== true) {
                return;
            }
            if (!this.emitCancelOnClickOutside) {
                this._active = false;
                return;
            }
            this._active = false;
            this.$emit('cancel');
        },
        renderFull() {
            return (
                <BackMask class="back-mask">
                    <Contents
                        class="dialog-contents"
                        hasContentsMaxWidth={this.hasContentsMaxWidth}
                        autoSize={this.autoSize}
                    >
                        <OutsideArea onClick={this.onClickOutside} />
                        {this.loadingProgress && (
                            <LoadingCover>
                                <Progress
                                    striped={true}
                                    percentage={100}
                                    show-text={false}
                                    stroke-width={6}
                                />
                            </LoadingCover>
                        )}
                        <ContentBox class="dialog-content-box">
                            <Content
                                class="dialog-content"
                                paddingLess={this.paddingLess}
                                autoSize={this.autoSize}
                            >
                                {this.hasCloseButton && (
                                    <CloseIcon
                                        v-view-box
                                        class="adriel-cursor-pointer close-btn"
                                        onClick={this.handleCancel}
                                    />
                                )}
                                {this.child}
                                {this.$slots.default}
                                {this.text && (
                                    <DIALOG_TEXT
                                        domPropsInnerHTML={this.text}
                                    />
                                )}
                            </Content>
                            {!this.hideButtons && (
                                <ButtonArea class="button-wrapper">
                                    {this.$slots.leftOfButton}
                                    {this.hasCancelButton && (
                                        <ActionButton
                                            class="cancel-button"
                                            type="cancel"
                                            onClick={this.handleCancel}
                                        >
                                            {this._cancelText}
                                        </ActionButton>
                                    )}
                                    {this.hasConfirmButton &&
                                        (this.$slots.confirmButton || (
                                            <ActionButton
                                                class="confirm-button"
                                                type={this.confirmType}
                                                isConfirm={true}
                                                onClick={async () => {
                                                    if (this.buttonDisabled)
                                                        return;
                                                    await this.confirmCb();
                                                    this.$emit('confirm');
                                                }}
                                                disabled={
                                                    this.buttonDisabled ||
                                                    this.loadingProgress
                                                }
                                            >
                                                {this.buttonLoading ? (
                                                    <RingLoader size={24} />
                                                ) : (
                                                    this._confirmText
                                                )}
                                            </ActionButton>
                                        ))}
                                </ButtonArea>
                            )}
                            {this.$slots.button && (
                                <ButtonArea>{this.$slots.button}</ButtonArea>
                            )}
                        </ContentBox>
                    </Contents>
                </BackMask>
            );
        },
        renderNormal() {
            return [
                <BackMask onClick={this.onClickOutside} />,
                <Contents
                    class="dialog-contents"
                    hasContentsMaxWidth={this.hasContentsMaxWidth}
                    autoSize={this.autoSize}
                >
                    {this.loadingProgress && (
                        <LoadingCover>
                            <Progress
                                striped={true}
                                percentage={100}
                                show-text={false}
                                stroke-width={6}
                            />
                        </LoadingCover>
                    )}
                    <ContentBox class="dialog-content-box">
                        <Content
                            class="dialog-content"
                            paddingLess={this.paddingLess}
                            autoSize={this.autoSize}
                        >
                            {this.title && (
                                <Title class="dialog-title">{this.title}</Title>
                            )}
                            {this.hasCloseButton && (
                                <CloseIcon
                                    v-view-box
                                    class="adriel-cursor-pointer close-btn"
                                    onClick={this.handleCancel}
                                />
                            )}
                            {this.child}
                            {this.$slots.default}
                            {this.text && (
                                <DIALOG_TEXT
                                    class="dialog-text"
                                    domPropsInnerHTML={this.text}
                                />
                            )}
                        </Content>
                        {!this.hideButtons && (
                            <ButtonArea class="button-wrapper">
                                {this.$slots.leftOfButton}
                                {this.hasCancelButton && (
                                    <ActionButton
                                        class="cancel-button"
                                        type="cancel"
                                        onClick={this.handleCancel}
                                    >
                                        {this._cancelText}
                                    </ActionButton>
                                )}
                                {this.hasConfirmButton &&
                                    (this.$slots.confirmButton || (
                                        <ActionButton
                                            class="confirm-button"
                                            type={this.confirmType}
                                            isConfirm={true}
                                            onClick={async () => {
                                                if (this.buttonDisabled) return;
                                                await this.confirmCb();
                                                this.$emit('confirm');
                                            }}
                                            disabled={
                                                this.buttonDisabled ||
                                                this.loadingProgress
                                            }
                                        >
                                            {this.buttonLoading ? (
                                                <RingLoader size={24} />
                                            ) : (
                                                this._confirmText
                                            )}
                                        </ActionButton>
                                    ))}
                            </ButtonArea>
                        )}
                        {this.$slots.button && (
                            <ButtonArea>{this.$slots.button}</ButtonArea>
                        )}
                    </ContentBox>
                </Contents>,
            ];
        },
    },
};

const Wrapper = styled(FLEX_COLUMN_DIV, {
    active: Boolean,
    isFullOnMobile: Boolean,
    newDesign: Boolean,
    customCss: String,
})`
    align-items: center;
    justify-content: center;
    overflow: hidden;
    z-index: 1001;
    ${ABSOLUTE_FULL_AREA};
    position: fixed;
    display: ${({ active }) => (active ? 'flex' : 'none')};
    height: 100%;

    &.scroll-on-body {
        .dialog-contents {
            padding-top: 60px;
            padding-bottom: 60px;
            display: flex;
            justify-content: center;
        }

        .dialog-content {
            max-height: initial;
        }

        .dialog-content-box {
            overflow-y: visible;
        }

        .back-mask {
            overflow-y: auto;
            overflow-x: hidden;
        }
    }

    ${({ isFullOnMobile }) =>
        isFullOnMobile &&
        css`
            @media screen and (max-width: 767px) {
                .dialog- {
                    &contents {
                        width: 100vw;
                        height: 100vh;
                    }

                    &content {
                        width: 100vw;
                        height: 100vh;
                        max-height: initial;
                    }
                }
            }
        `};

    ${({ newDesign }) => newDesign && newDialogCss}
    ${({ customCss }) => customCss}
`;

const newDialogCss = css`
    .dialog-content-box {
        min-width: unset;
        border-radius: 12px;

        & > div {
            padding: 16px;
        }
    }

    .dialog-content,
    .button-wrapper {
        padding: 16px;
    }

    .button-wrapper {
        height: unset;
        gap: 8px;
    }

    .cancel-button,
    .confirm-button {
        height: 40px;
        font-size: 16px;
        font-weight: 400;
        padding: 0 10px;
        border-radius: 8px;
        letter-spacing: -0.01em;
    }

    .cancel-button {
        color: #3c464f;
        border: 1px solid #e2e7eb;
    }

    .confirm-button {
        margin-left: unset !important;
        background-color: #2c8dff;

        &:hover,
        &:active {
            background-color: #0056f2;
        }
    }
`;

const BackMask = styled.div`
    background-color: rgba(0, 0, 0, 0.5);
    ${ABSOLUTE_FULL_AREA};
    @media screen and (max-width: 767px) {
        background-color: rgba(0, 0, 0, 0.2);
    }
`;

const Contents = styled('div', {
    hasContentsMaxWidth: Boolean,
    autoSize: Boolean,
})`
    position: relative;
    ${({ hasContentsMaxWidth, autoSize }) =>
        hasContentsMaxWidth &&
        !autoSize &&
        css`
            max-width: 1000px;
        `};
    margin: 0 20px;
    z-index: 1;

    ${RespondMobile`
        ${RespondLandscape`
        margin-left: calc(20px + constant(safe-area-inset-left));
        margin-left: calc(20px + env(safe-area-inset-left));
        margin-right: calc(20px + constant(safe-area-inset-right));
        margin-right: calc(20px + env(safe-area-inset-right));
        `}
    `}
`;

const ContentBox = styled.div`
    border-radius: 4px;
    box-shadow: 0 16px 48px 0 rgba(0, 0, 0, 0.16);
    background-color: white;
    overflow-y: auto;
    overflow-x: hidden;

    ${RespondPC`
        min-width: 528px;
    `}

    ${RespondMobile`
        /* min-width: initial; */
        max-height: 100vh;
        max-width: 600px;
    `}
`;

const Title = styled.h3`
    color: #1c232b;

    font-family: var(--font-family-primary);
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 125% */
    letter-spacing: -0.08px;
`;

const Content = styled('div', { paddingLess: Boolean, autoSize: Boolean })`
    padding: ${({ paddingLess }) => (paddingLess ? 0 : '24px')};
    overflow-y: auto;

    ${({ autoSize }) =>
        !autoSize &&
        css`
            max-height: calc(100vh - 250px);
        `};
    /* max-height: calc(100vh - 250px); */
    position: relative;
    @media ${ON_MOBILE} {
        max-height: calc(100vh - 150px);
    }

    .close-btn {
        position: absolute;
        width: 20px;
        height: 20px;
        top: 20px;
        right: 20px;
        fill: ${GRAY(40)};
    }
`;

export const ButtonArea = styled(FLEX_VERTICAL_CENTER_DIV)`
    justify-content: flex-end;
    padding-right: 24px;
    height: 84px;
    border-top: 1px solid ${COLORS['gray-25']};

    & > button:last-child {
        margin-left: 8px;
        ${RespondMobile`
            margin-left: 0px;
        `}
    }
    ${RespondMobile`
        flex-direction: column;
        padding: 16px 20px;
        height: auto;
        & > button {
            margin: 4px 0;
        }
    `}
`;

const _BUTTON_MAP = {
    cancel: css`
        color: ${COLORS['gray-80']};
        ${applyHoverAndPress('white', 'background-color')};
    `,
    primary: css`
        color: white;
        ${applyHoverAndPress(COLORS['blue-60'], 'background-color')};
        &:disabled {
            background-color: ${COLORS['blue-30']};
            cursor: unset;
        }
    `,
    danger: css`
        color: white;
        ${applyHoverAndPress(COLORS['red-60'], 'background-color')};
        &:disabled {
            background-color: ${COLORS['red-10']};
            cursor: unset;
        }
    `,
    secondary: css`
        color: ${COLORS['gray-80']};
        ${applyHoverAndPress('white', 'background-color')};
        border: 1px solid ${COLORS['gray-30']};
    `,
};

export const ActionButton = styled('button', {
    type: String,
    isConfirm: Boolean,
    disabled: Boolean,
})`
    ${FLEX_CENTER};
    height: 44px;
    min-width: 120px;
    padding: 0 36px;
    font-size: 14px;
    font-weight: 600;
    transition: 0.2s background-color;
    border-radius: 2px;
    ${({ type = 'primary' }) => _BUTTON_MAP[type]};
    ${({ disabled, type }) => {
        if (!disabled) {
            return;
        }

        return type !== 'danger'
            ? css`
                  background-color: ${COLORS['blue-30']};
                  cursor: initial;
                  color: ${COLORS['blue-10']};
                  &:hover,
                  &:active {
                      background-color: ${COLORS['blue-30']};
                  }
              `
            : css`
                  background-color: ${COLORS['red-10']};
                  cursor: initial;
                  color: ${COLORS['white']};
                  &:hover,
                  &:active {
                      background-color: ${COLORS['red-10']};
                  }
              `;
    }}
    ${RespondMobile`
        min-width: initial;
        width: 100%;
        ${({ isConfirm }) =>
            isConfirm &&
            css`
                order: -1;
            `};
    `}
`;

const LoadingCover = styled(FLEX_CENTER_DIV)`
    ${ABSOLUTE_FULL_AREA};
    background-color: transparent;
    z-index: 3;

    .vm-progress {
        position: absolute;
        left: 4px;
        right: 4px;
        top: 4px;

        .vm-progress-bar__inner {
            background-color: ${COLORS['blue-30']};
        }
    }
`;

const OutsideArea = styled.div`
    position: absolute;
    left: -50px;
    right: -50px;
    top: 0px;
    bottom: 0px;
`;
